import * as React from 'react'
import styled from 'styled-components'
import { DatItem } from './DatItem'
import { DatLabelText } from './DatLabelText'

const DatItemColor = styled(DatItem)`
  --dat-accent: rgba(148, 163, 184, 0.42);
`

const ColorField = styled.div`
  flex: 1 1 62%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  @media (max-width: 520px) {
    flex-basis: 100%;
    justify-content: space-between;
  }
`

const ColorInput = styled.input.attrs({ type: 'color' })`
  width: 34px;
  height: 34px;
  padding: 3px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 11px;
  background: rgba(2, 6, 23, 0.46);
  cursor: pointer;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: 0;
    border-radius: 8px;
  }

  &::-moz-color-swatch {
    border: 0;
    border-radius: 8px;
  }
`

const ColorValue = styled.span`
  font-size: 0.8rem;
  font-family: 'IBM Plex Mono', 'Consolas', 'SFMono-Regular', monospace;
  font-variant-numeric: tabular-nums;
  color: rgba(148, 163, 184, 0.84);
`

const COLOR_COMMIT_DELAY_MS = 240

type Props = {
  label: React.ReactNode
  value: string
  onChange: (color: string) => void
}

export const DatColor = (props: Props) => {
  const { label, onChange, value } = props
  const [draftColor, setDraftColor] = React.useState(value)
  const commitTimerRef = React.useRef<number | null>(null)

  const clearCommitTimer = React.useCallback(() => {
    if (commitTimerRef.current != null) {
      window.clearTimeout(commitTimerRef.current)
      commitTimerRef.current = null
    }
  }, [])

  const commitColor = React.useCallback(
    (color: string) => {
      clearCommitTimer()

      if (color !== value) {
        onChange(color)
      }
    },
    [clearCommitTimer, onChange, value]
  )

  const scheduleCommit = React.useCallback(
    (color: string) => {
      clearCommitTimer()

      commitTimerRef.current = window.setTimeout(() => {
        commitTimerRef.current = null

        if (color !== value) {
          onChange(color)
        }
      }, COLOR_COMMIT_DELAY_MS)
    },
    [clearCommitTimer, onChange, value]
  )

  React.useEffect(() => {
    clearCommitTimer()
    setDraftColor(value)
  }, [clearCommitTimer, value])

  React.useEffect(() => clearCommitTimer, [clearCommitTimer])

  return (
    <DatItemColor>
      <DatLabelText>{label}</DatLabelText>

      <ColorField>
        <ColorInput
          aria-label={typeof label === 'string' ? label : 'Background color'}
          value={draftColor}
          onBlur={event => commitColor(event.currentTarget.value)}
          onChange={event => {
            setDraftColor(event.currentTarget.value)
            scheduleCommit(event.currentTarget.value)
          }}
        />
        <ColorValue>{draftColor.toUpperCase()}</ColorValue>
      </ColorField>
    </DatItemColor>
  )
}
